export const SITE_URL =
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? "https://www.axy.digital";

export const BRAND_NAME = "Axy.digital";
// Registered legal entity + human/search-friendly alias. Both feed the
// Organization JSON-LD so engines can disambiguate the short token "Axy".
export const LEGAL_NAME = "Auxetic Studio Pte. Ltd.";
export const ALTERNATE_NAME = "Axy Digital";
// One-line Organization.description (distinct from the SoftwareApplication copy).
export const ORG_DESCRIPTION =
  "Axy.digital is an AI-native marketing platform that drives organic traffic and AI search visibility for lean businesses. Designed to run the full SEO and GEO workflow with minimal manual input, delivering agency-level outcomes at 1% of cost.";
// ISO 3166-1 alpha-2 country for the Organization PostalAddress.
export const ORG_COUNTRY = "SG";
export const TWITTER_HANDLE = "@axydigital_ai";
export const DEFAULT_OG_ALT = "Axy.digital — the autonomous algorithmic marketing engine.";

export const ORG_ID = `${SITE_URL}/#axy`;
export const ORG_LOGO = `${SITE_URL}/logo-black.png`;
// Stable @id for the WebSite node emitted once in __root.tsx. Article nodes
// point at it via `isPartOf` instead of re-declaring the site.
export const WEBSITE_ID = `${SITE_URL}/#website`;

export type SocialPlatform = "linkedin" | "x" | "tiktok" | "instagram" | "youtube";

// Social channels rendered as icons in the footer. Every `platform` here MUST
// have a matching icon in `SOCIAL_ICONS` (src/components/site-footer.tsx) — a
// missing icon renders `undefined` and crashes SSR.
export const SOCIAL_LINKS: { platform: SocialPlatform; label: string; url: string }[] = [
  { platform: "linkedin", label: "LinkedIn", url: "https://www.linkedin.com/company/axy-digital" },
  { platform: "x", label: "X", url: "https://x.com/axydigital_ai" },
  { platform: "tiktok", label: "TikTok", url: "https://www.tiktok.com/@axy.digital" },
  { platform: "instagram", label: "Instagram", url: "https://www.instagram.com/axy.digital_ai/" },
  { platform: "youtube", label: "YouTube", url: "https://www.youtube.com/@Axy.digital" },
];

// Entity/authority profiles that feed JSON-LD `sameAs` for brand disambiguation
// but are NOT shown as footer icons (no brand icon, and they aren't social
// channels). Add here — not to SOCIAL_LINKS — to avoid the footer icon coupling.
export const SAMEAS_PROFILES: { label: string; url: string }[] = [
  { label: "Crunchbase", url: "https://www.crunchbase.com/organization/axy-digital" },
  { label: "G2", url: "https://www.g2.com/products/axy-digital/reviews" },
  { label: "Trustpilot", url: "https://www.trustpilot.com/review/axy.digital" },
  { label: "Medium", url: "https://medium.com/@axy-digital" },
];

// Feeds JSON-LD `sameAs`: every owned profile (social + entity/authority).
export const SOCIAL_URLS = [
  ...SOCIAL_LINKS.map((s) => s.url),
  ...SAMEAS_PROFILES.map((s) => s.url),
];

export const CONTACT_EMAIL = "vibes@axy.digital";
export const FOUNDING_DATE = "2025";

// Freshness signal for AI crawlers (WebPage.dateModified). ISO 8601.
// Injected at build time from the last git commit date via Vite `define`
// (see vite.config.ts) so it updates automatically on every push. Falls back
// to the current date if the build-time constant is unavailable.
declare const __SITE_LAST_UPDATED__: string | undefined;
export const SITE_LAST_UPDATED =
  typeof __SITE_LAST_UPDATED__ !== "undefined"
    ? __SITE_LAST_UPDATED__
    : new Date().toISOString().slice(0, 10);

export const AUTHOR_ID = `${SITE_URL}/#robin-lim`;
export const AUTHOR_NAME = "Robin Lim";
export const AUTHOR_TITLE = "CEO & Co-founder of Axy.digital";
// Bio left empty until provided — fill in when the author bio is ready.
export const AUTHOR_BIO = "";

export type Founder = {
  id: string;
  name: string;
  title: string;
  bio?: string; // optional schema.org Person.description
  url?: string; // optional personal/profile URL; defaults to SITE_URL
  sameAs?: string[]; // optional social profile URLs (LinkedIn, X, …)
};

// Robin Lim is also the default content author (see AUTHOR_* above).
export const FOUNDERS: Founder[] = [
  {
    id: AUTHOR_ID,
    name: AUTHOR_NAME,
    title: AUTHOR_TITLE,
    bio: AUTHOR_BIO || undefined,
    sameAs: ["https://www.linkedin.com/in/robinlfm/"],
  },
  {
    id: `${SITE_URL}/#floriane-le-floch`,
    name: "Floriane Le Floch",
    title: "CTO & Co-founder of Axy.digital",
    sameAs: ["https://www.linkedin.com/in/floriane-le-floch/"],
  },
];

// Resolves a free-text Sanity `author` string to a Founder, case- and
// whitespace-insensitively. Robin Lim is the default byline when the name is
// absent or unknown.
export function founderByName(name?: string): Founder {
  const key = (name ?? "").trim().toLowerCase();
  return FOUNDERS.find((f) => f.name.toLowerCase() === key) ?? FOUNDERS[0];
}
